import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, FolderTree, ChevronRight, ChevronDown, RefreshCw } from 'lucide-react';
import apiClient from '../services/api';

// ── shared navy colours (matches sidebar) ─────────────────────────────────
const NAVY      = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const AccountGroups = () => {

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session     = getSessionData();
  const companyId   = session?.companyid;
  const yearId      = session?.yearid;
  const companyName = session?.companyname || session?.company_name || '';

  // ── state ──────────────────────────────────────────────────────────────
  const [groups,         setGroups]         = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [treeData,       setTreeData]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [filterType,     setFilterType]     = useState('all');
  const [showModal,      setShowModal]      = useState(false);
  const [editMode,       setEditMode]       = useState(false);
  const [expandedNodes,  setExpandedNodes]  = useState(new Set());
  const [submitLoading,  setSubmitLoading]  = useState(false);
  const [errors,         setErrors]         = useState({});

  const [formData, setFormData] = useState({
    acgroupid: null, companyid: companyId || null,
    acgroupcode: '', acgroupname: '', actype: 'Asset',
    acclass: '', parentid: '', grouporder: 0, statushide: false,
  });

  const accountTypes = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];

  const accountClasses = {
    Asset:     ['Current Asset', 'Fixed Asset', 'Intangible Asset', 'Other Asset'],
    Liability: ['Current Liability', 'Long Term Liability'],
    Equity:    ['Capital', 'Retained Earnings', 'Drawings'],
    Income:    ['Operating Income', 'Non-Operating Income', 'Other Income'],
    Expense:   ['Cost of Sales', 'Operating Expense', 'Administrative Expense', 'Financial Expense'],
  };

  const typeColors = {
    Asset:     { bg: '#10b981', light: '#d1fae5', text: '#065f46' },
    Liability: { bg: '#ef4444', light: '#fee2e2', text: '#991b1b' },
    Equity:    { bg: '#3b82f6', light: '#dbeafe', text: '#1e40af' },
    Income:    { bg: '#8b5cf6', light: '#ede9fe', text: '#5b21b6' },
    Expense:   { bg: '#f59e0b', light: '#fef3c7', text: '#92400e' },
  };

  // ── lifecycle ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchGroups();
  }, [companyId, yearId]);

  useEffect(() => { filterGroups(); }, [searchTerm, filterType, groups]);

  // ── data ───────────────────────────────────────────────────────────────
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Acgroups.php', { params: { companyid: companyId, statushide: 0 } });
      if (res.data.success) {
        setGroups(res.data.data || []);
        buildTreeData(res.data.data || []);
      } else {
        alert('Failed to fetch account groups: ' + res.data.message);
      }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error connecting to backend.');
    } finally { setLoading(false); }
  };

  const buildTreeData = (flat) => {
    const map = {};
    flat.forEach(i => { map[i.acgroupid] = { ...i, children: [] }; });
    const tree = [];
    flat.forEach(i => {
      if (i.parentid == null) tree.push(map[i.acgroupid]);
      else if (map[i.parentid]) map[i.parentid].children.push(map[i.acgroupid]);
    });
    setTreeData(tree);
  };

  const filterGroups = () => {
    let f = groups;
    if (filterType !== 'all') f = f.filter(g => g.actype === filterType);
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      f = f.filter(g =>
        g.acgroupname?.toLowerCase().includes(s) ||
        g.acgroupcode?.toLowerCase().includes(s) ||
        g.acclass?.toLowerCase().includes(s)
      );
    }
    setFilteredGroups(f);
  };

  // ── tree ───────────────────────────────────────────────────────────────
  const toggleNode = (id) => {
    const n = new Set(expandedNodes);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpandedNodes(n);
  };

  const renderTree = (nodes, level = 0) => nodes.map(node => {
    const tc = typeColors[node.actype] || { bg: '#6b7280', light: '#f3f4f6', text: '#374151' };
    return (
      <div key={node.acgroupid} style={{ marginLeft: `${level * 24}px` }}>
        {/* Tree row */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg mb-1 border transition-all duration-150 cursor-default"
          style={{ background: node.statushide ? '#fef2f2' : '#fff', borderColor: '#f0f0f0' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ff'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
          onMouseLeave={e => { e.currentTarget.style.background = node.statushide ? '#fef2f2' : '#fff'; e.currentTarget.style.borderColor = '#f0f0f0'; }}
        >
          {/* Expand toggle */}
          <span
            className="flex items-center text-gray-400 flex-shrink-0 w-4 cursor-pointer"
            onClick={() => node.children?.length > 0 && toggleNode(node.acgroupid)}
          >
            {node.children?.length > 0
              ? (expandedNodes.has(node.acgroupid) ? <ChevronDown size={15} /> : <ChevronRight size={15} />)
              : null}
          </span>

          <FolderTree size={15} style={{ color: tc.bg, flexShrink: 0 }} />

          <span className="flex-1 font-semibold text-[13.5px] text-gray-800">{node.acgroupname}</span>

          <span className="text-[11px] text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
            {node.acgroupcode}
          </span>

          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: tc.light, color: tc.text }}>
            {node.actype}
          </span>

          {node.acclass && (
            <span className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full">
              {node.acclass}
            </span>
          )}

          <span className="text-[11px] text-gray-400 whitespace-nowrap">
            {node.childcount || 0} sub · {node.headcount || 0} heads
          </span>

          {/* Actions */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={e => { e.stopPropagation(); handleEdit(node); }}
              className="w-7 h-7 flex items-center justify-center rounded-md border-0 cursor-pointer
                bg-violet-100 text-violet-700 transition-all duration-150
                hover:bg-violet-700 hover:text-white"
              title="Edit"
            >
              <Edit2 size={13} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); handleDelete(node.acgroupid); }}
              className="w-7 h-7 flex items-center justify-center rounded-md border-0 cursor-pointer
                bg-red-100 text-red-600 transition-all duration-150
                hover:bg-red-600 hover:text-white"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>

        {expandedNodes.has(node.acgroupid) && node.children?.length > 0 &&
          renderTree(node.children, level + 1)}
      </div>
    );
  });

  // ── CRUD ───────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.acgroupname.trim()) errs.acgroupname = 'Group name is required';
    if (!formData.actype)            errs.actype       = 'Account type is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/Acgroups.php', {
        ...formData, companyid: companyId, statushide: formData.statushide ? 1 : 0,
      });
      if (res.data.success) {
        if (!editMode && res.data.data?.acgroupcode)
          alert(`Account group created!\nCode: ${res.data.data.acgroupcode}`);
        else
          alert(editMode ? 'Account group updated!' : 'Account group created!');
        closeModal(); fetchGroups();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving account group. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (group) => {
    setFormData({
      acgroupid: group.acgroupid, companyid: group.companyid,
      acgroupcode: group.acgroupcode, acgroupname: group.acgroupname,
      actype: group.actype, acclass: group.acclass || '',
      parentid: group.parentid || '', grouporder: group.grouporder || 0,
      statushide: group.statushide === 1,
    });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account group?')) return;
    try {
      const res = await apiClient.delete('/Acgroups.php', { data: { acgroupid: id } });
      if (res.data.success) { alert('Account group deleted!'); fetchGroups(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error deleting account group. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({ acgroupid: null, companyid: companyId || null, acgroupcode: '', acgroupname: '', actype: 'Asset', acclass: '', parentid: '', grouporder: 0, statushide: false });
    setEditMode(false); setErrors({});
  };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  // shared input class
  const inputCls = (hasError) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#4f46e5] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]
    ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  const selectCls = (hasError) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#4f46e5] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]
    disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
    ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <FolderTree size={40} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── PAGE HEADER ── */}
      <div
        className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <FolderTree size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>
              Account Groups
            </h1>
            <p className="text-[13px] flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Chart of accounts structure
              {companyName && (
                <span className="bg-white/20 text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full">
                  {companyName}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={fetchGroups}
            disabled={loading}
            className="w-10 h-10 flex items-center justify-center rounded-lg border cursor-pointer
              bg-white/15 border-white/35 text-white transition-all duration-150
              hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0
              text-sm font-bold cursor-pointer transition-all duration-150
              hover:bg-violet-50"
            style={{ color: NAVY }}
          >
            <Plus size={17} />
            Add Group
          </button>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        {/* Search */}
        <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#4f46e5] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]
          transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by name, code, or class..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium
              placeholder:text-gray-400 placeholder:font-normal"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter */}
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-800
            bg-white cursor-pointer outline-none min-w-[140px]
            hover:border-[#4f46e5] focus:border-[#4f46e5] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)]
            transition-all duration-200"
        >
          <option value="all">All Types</option>
          {accountTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {/* Count */}
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          {filteredGroups.length} {filteredGroups.length === 1 ? 'group' : 'groups'}
        </div>
      </div>

      {/* ── TREE BODY ── */}
      <div className="flex-1 p-5 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading account groups...
          </div>
        ) : treeData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <p className="text-xl text-gray-400 font-semibold mb-4">No account groups found.</p>
            <button
              onClick={openAddModal}
              className="px-6 py-3 text-white font-bold rounded-lg border-0 cursor-pointer
                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}
            >
              Create First Group
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            {renderTree(treeData)}
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          <div
            className="bg-white rounded-2xl w-full shadow-2xl border border-gray-200 overflow-hidden
              max-h-[90vh] overflow-y-auto"
            style={{ maxWidth: 680, animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)` }}
            >
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide"
                style={{ color: '#fff', margin: 0 }}>
                <FolderTree size={20} />
                {editMode ? 'Edit Account Group' : 'New Account Group'}
              </h3>
              <button
                onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0
                  bg-white/20 border-white/30 text-white transition-all duration-200
                  hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal form */}
            <form onSubmit={handleSubmit} className="p-7">

              {/* Auto-code banner / code display */}
              {!editMode ? (
                <div className="flex items-center gap-2.5 px-4 py-3 mb-5 rounded-lg text-[13.5px] font-semibold text-indigo-800
                  bg-gradient-to-r from-blue-50 to-violet-50 border border-indigo-200">
                  <span>ℹ️</span>
                  <span>Group code will be auto-generated (AG001, AG002, …)</span>
                </div>
              ) : formData.acgroupcode && (
                <div className="mb-5">
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Group Code</label>
                  <input type="text" value={formData.acgroupcode} disabled
                    className="w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-100 cursor-not-allowed" />
                </div>
              )}

              {/* Name + Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Group Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.acgroupname}
                    onChange={e => setFormData({ ...formData, acgroupname: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Current Assets"
                    className={inputCls(errors.acgroupname)}
                  />
                  {errors.acgroupname && (
                    <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.acgroupname}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Account Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.actype}
                    onChange={e => setFormData({ ...formData, actype: e.target.value, acclass: '' })}
                    className={selectCls(errors.actype)}
                  >
                    <option value="">-- Select Type --</option>
                    {accountTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {errors.actype && (
                    <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.actype}</p>
                  )}
                </div>
              </div>

              {/* Class + Parent */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Account Class</label>
                  <select
                    value={formData.acclass}
                    onChange={e => setFormData({ ...formData, acclass: e.target.value })}
                    disabled={!formData.actype}
                    className={selectCls(false)}
                  >
                    <option value="">-- Select Class --</option>
                    {formData.actype && accountClasses[formData.actype]?.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Parent Group</label>
                  <select
                    value={formData.parentid}
                    onChange={e => setFormData({ ...formData, parentid: e.target.value })}
                    className={selectCls(false)}
                  >
                    <option value="">-- None (Top Level) --</option>
                    {groups
                      .filter(g => g.acgroupid !== formData.acgroupid && g.actype === formData.actype)
                      .map(g => (
                        <option key={g.acgroupid} value={g.acgroupid}>
                          {g.acgroupname} ({g.acgroupcode})
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Order + Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Display Order</label>
                  <input
                    type="number"
                    value={formData.grouporder}
                    onChange={e => setFormData({ ...formData, grouporder: e.target.value })}
                    min="0"
                    className={inputCls(false) + ' text-right'}
                  />
                </div>

                <div className="flex items-end pb-0.5">
                  <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50
                    border-2 border-gray-200 rounded-lg w-full hover:bg-gray-100 hover:border-gray-300
                    transition-all duration-150">
                    <input
                      type="checkbox"
                      checked={formData.statushide}
                      onChange={e => setFormData({ ...formData, statushide: e.target.checked })}
                      className="w-5 h-5 cursor-pointer accent-[#4f46e5]"
                    />
                    <span className="text-sm font-semibold text-gray-700">Hide / Inactive</span>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={submitLoading}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-bold
                    text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer
                    transition-all duration-200 hover:bg-gray-50 hover:border-gray-300
                    disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-bold
                    text-white border-0 rounded-lg cursor-pointer
                    transition-all duration-200 hover:-translate-y-0.5
                    hover:shadow-[0_6px_16px_rgba(79,70,229,0.35)]
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)', color: '#fff' }}
                >
                  {submitLoading ? 'Saving…' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96); opacity:0 } to { transform:translateY(0) scale(1); opacity:1 } }
      `}</style>
    </div>
  );
};

export default AccountGroups;